import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, ArrowUpRight } from "lucide-react";
import { AI_TOOLS } from "@/data/homepageData";

/** Section 4 — AI Tools: 6 premium cards, each linking to its own dedicated page
 * (never expand the tool inline here — homepage showcases, dedicated pages explain). */
export const AIToolsSection = () => {
  return (
    <section className="max-w-6xl mx-auto px-6 py-16" data-testid="ai-tools-section">
      <div className="max-w-2xl mb-12">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#B58BFF] flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5" /> AI Tools
        </span>
        <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-3">Six tools. One career OS.</h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {AI_TOOLS.map((t, i) => {
          const isAnchor = t.route.startsWith("#");
          const CardTag = isAnchor ? "a" : Link;
          const cardProps = isAnchor ? { href: t.route } : { to: t.route };
          return (
            <motion.div
              key={t.key} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: (i % 3) * 0.07 }}
              whileHover={{ y: -6 }}
            >
              <CardTag
                {...cardProps} data-testid={`ai-tool-card-${t.key}`}
                className="group relative flex flex-col h-full p-6 rounded-3xl border overflow-hidden transition-colors duration-300"
                style={{ borderColor: "rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.02)" }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = `${t.color}60`; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; }}
              >
                <div
                  className="absolute -top-12 -right-12 w-40 h-40 rounded-full opacity-0 group-hover:opacity-100 blur-3xl transition-opacity duration-500 pointer-events-none"
                  style={{ background: t.color }}
                />
                <div className="relative flex items-center justify-between mb-5">
                  <div
                    className="w-12 h-12 rounded-2xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
                    style={{ background: `${t.color}1f`, border: `1px solid ${t.color}40`, boxShadow: `0 0 22px ${t.color}25` }}
                  >
                    <t.icon className="w-5 h-5" style={{ color: t.color }} strokeWidth={1.75} />
                  </div>
                  <ArrowUpRight className="relative w-4 h-4 text-slate-500 group-hover:text-white group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                </div>
                <p className="relative font-heading text-base font-semibold text-white">{t.title}</p>
                <p className="relative text-sm text-slate-400 mt-2 leading-relaxed">{t.desc}</p>
                <span
                  className="relative inline-flex items-center gap-1 text-xs font-semibold mt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{ color: t.color }}
                >
                  Open tool <ArrowUpRight className="w-3 h-3" />
                </span>
              </CardTag>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
};
